import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { X, Gift } from 'lucide-react';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

// Static demo QR payload — mirrors backend's ALLURE_DEMO_QR_PAYLOAD.
// Keeping it the same for every user so this image can be dropped into
// the pitch deck PPTX once and scanned forever.
const DEMO_QR_PAYLOAD = 'ALLURE-DEMO-TRAINING-2026';
const DEMO_PASS_ID = 'DEMO01';

export default function DemoQrPage() {
  const navigate = useNavigate();
  const lastSeenRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    let active = true;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/demo-transactions/latest-pending`, { headers });
        if (!active) return;
        const incoming = res.data?.transaction;
        if (res.data?.pending && incoming?.id && incoming.id !== lastSeenRef.current) {
          lastSeenRef.current = incoming.id;
          navigate(`/demo-scan/pay/${incoming.id}`);
        }
      } catch (_) {
        /* ignore — demo page, no retries needed */
      }
    };

    poll();
    const iv = setInterval(poll, 2000);
    return () => { active = false; clearInterval(iv); };
  }, [navigate]);

  return (
    <div className="app-shell" data-testid="demo-qr-page" style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 56px)', paddingLeft: 24, paddingRight: 24, paddingBottom: 60, position: 'relative' }}>
      <button
        type="button"
        onClick={() => navigate('/demo-scan')}
        aria-label="Close demo QR"
        data-testid="demo-qr-close"
        style={{ position: 'absolute', top: 'max(14px, env(safe-area-inset-top))', right: 18, width: 40, height: 40, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}
      >
        <X size={20} color="var(--text)" />
      </button>

      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={90} dataTestId="demo-qr-logo" />
      </div>

      <h1 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: 'var(--text)', textAlign: 'center', marginTop: 16, marginBottom: 4 }}>
        Demo guest QR
      </h1>
      <p style={{ fontFamily: FONT, fontSize: 13, color: 'var(--text-2)', textAlign: 'center', margin: 0 }}>
        Have someone scan this with the Allure venue app
      </p>

      <div data-testid="demo-qr-code-card" style={{ marginTop: 26, position: 'relative', width: 260, height: 260, borderRadius: 24, background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.08)', boxShadow: 'var(--premium-shadow-nested)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <QRCodeSVG value={DEMO_QR_PAYLOAD} size={220} level="H" bgColor="#FFFFFF" fgColor="#000000" style={{ display: 'block' }} />
        <div aria-hidden style={{ position: 'absolute', top: -18, right: -10, background: '#1F8E2A', color: '#FFFFFF', fontFamily: FONT, fontSize: 14, fontWeight: 700, padding: '6px 12px', borderRadius: 9999, boxShadow: '0 6px 14px rgba(31, 142, 42, 0.32)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Gift size={13} strokeWidth={2.4} /> 8% OFF
        </div>
      </div>

      <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: 'calc(100% - 20px)', maxWidth: 300 }}>
        <span style={{ fontFamily: FONT, fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 700 }}>Pass id</span>
        <span data-testid="demo-qr-pass-id" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 15, color: 'var(--text)', letterSpacing: 1.5 }}>
          {DEMO_PASS_ID}
        </span>
      </div>

      <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#1F8E2A', boxShadow: '0 0 0 4px rgba(31, 142, 42, 0.18)', animation: 'demo-pulse 1.6s ease-in-out infinite' }} />
        <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>
          Waiting for the venue to scan…
        </span>
      </div>

      <p style={{ marginTop: 'auto', paddingTop: 32, fontFamily: FONT, fontSize: 11, color: 'var(--text-3)', textAlign: 'center' }}>
        Demo mode · No real payment is processed.
      </p>

      <style>{`@keyframes demo-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}
